import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { TaskRun } from '@/types/robotEvents'
import type { FrontendLog } from '@/types/frontendLog'
import type { TrajectoryInfo } from '@/types/forklift'
import { formatDuration } from '@/common/duration'
import PreviewPathCanvas from '@/components/maps/PreviewPathCanvas'
import { useScenarioTrajectory } from '@/hooks/useScenarioTrajectory'

// トリガー別バッジ (機体イベントジャーナルの trigger)
const TRIGGER_LABELS: Record<string, { label: string; className: string }> = {
  ui: { label: 'UI', className: 'bg-blue-100 text-blue-800' },
  api: { label: 'API', className: 'bg-purple-100 text-purple-800' },
  plc: { label: 'PLC', className: 'bg-teal-100 text-teal-800' },
  command: { label: '指令', className: 'bg-indigo-100 text-indigo-800' },
  unknown: { label: '単発', className: 'bg-gray-100 text-gray-600' },
}

const REASON_LABELS: Record<string, { label: string; className: string }> = {
  completed: { label: '完了', className: 'bg-green-100 text-green-800' },
  canceled: { label: '中断', className: 'bg-red-100 text-red-800' },
  error_stopped: {
    label: 'エラー停止',
    className: 'bg-orange-100 text-orange-800',
  },
}

const EXECUTION_MODE_LABELS: Record<string, string> = {
  initialize: '最初から実行',
  continue: '続きから実行',
  from_slot: '開始スロット指定',
  api: 'API',
}

function formatTime(ts: string | null): string {
  if (!ts) return '－'
  return new Date(ts).toLocaleTimeString('ja-JP', { hour12: false })
}

function taskLabel(taskType: string, srcId: string, dstId: string): string {
  const target = [srcId, dstId].filter(Boolean).join(' → ')
  return target ? `${taskType} ${target}` : taskType
}

// 実行に対応する scenario_execute ログのルートプレビューとコンテキスト。
// trajectories は context に残っていればそれを使い、ロールアップで
// 落とされている場合は退避テーブルから取得する (LogDetailModal と同じ方式)。
function RunScenarioDetail({
  log,
  onOpenLog,
}: {
  log: FrontendLog
  onOpenLog?: (log: FrontendLog) => void
}) {
  const inlineTrajectories = Array.isArray(log.context?.trajectories)
    ? (log.context!.trajectories as TrajectoryInfo[])
    : null
  const { data: fetchedTrajectory } = useScenarioTrajectory(
    log.robot_id,
    log.log_id,
    !inlineTrajectories
  )
  const trajectories =
    inlineTrajectories ?? fetchedTrajectory?.trajectories ?? null

  return (
    <div className="mb-4">
      {trajectories && trajectories.length > 0 && (
        <div className="max-w-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">
            予定ルート（実行開始ログ）
          </p>
          <PreviewPathCanvas trajectories={trajectories} />
        </div>
      )}
      <div className="mt-3 flex items-start gap-4">
        {log.context && (
          <details className="text-sm flex-1 min-w-0">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
              コンテキスト
            </summary>
            <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
              {JSON.stringify(log.context, null, 2)}
            </pre>
          </details>
        )}
        {onOpenLog && (
          <button
            onClick={() => onOpenLog(log)}
            className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap"
          >
            ログ詳細を開く
          </button>
        )}
      </div>
    </div>
  )
}

interface TaskRunTableProps {
  runs: TaskRun[]
  truncated?: boolean
  // 行クリックで再生位置をシークする
  onSeek?: (time: Date) => void
  // 詳細画面右パネル用の省スペース表示 (終了/タスク数/実行時間の列を畳む)
  compact?: boolean
  // run.key -> 対応する scenario_execute ログ (プレビュー・コンテキスト表示用)
  runLogs?: Record<string, FrontendLog>
  // 対応ログの詳細モーダルを開く
  onOpenLog?: (log: FrontendLog) => void
}

export default function TaskRunTable({
  runs,
  truncated,
  onSeek,
  compact = false,
  runLogs,
  onOpenLog,
}: TaskRunTableProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const colCount = compact ? 5 : 8

  return (
    <div className="overflow-x-auto">
      {truncated && (
        <p className="px-6 pt-2 text-xs text-amber-600">
          イベント数が多いため一部のみ表示しています
        </p>
      )}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-8 px-3 py-3"></th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              開始
            </th>
            {!compact && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                終了
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              内容
            </th>
            {!compact && (
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                タスク
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              結果
            </th>
            {!compact && (
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                実行時間
              </th>
            )}
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              エラー
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {runs.map((run) => {
            const trigger =
              TRIGGER_LABELS[run.trigger] ?? TRIGGER_LABELS.unknown
            const reason = run.reason ? REASON_LABELS[run.reason] : null
            const isExpanded = expandedKey === run.key
            const hasDetail = run.tasks.length > 0 || run.errors.length > 0
            return (
              <>
                <tr
                  key={run.key}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    if (hasDetail) {
                      setExpandedKey(isExpanded ? null : run.key)
                    }
                    if (run.startTs && onSeek) onSeek(new Date(run.startTs))
                  }}
                >
                  <td className="px-3 py-3 text-gray-400">
                    {hasDetail &&
                      (isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      ))}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatTime(run.startTs)}
                  </td>
                  {!compact && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatTime(run.endTs)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mr-2 ${trigger.className}`}
                    >
                      {trigger.label}
                    </span>
                    {run.isScenario
                      ? run.scenarioName || run.scenarioId || '不明'
                      : '単発実行'}
                    {run.reconstructed && (
                      <span
                        className="inline-flex px-2 py-0.5 rounded text-xs font-medium ml-2 bg-amber-50 text-amber-700 border border-amber-200"
                        title="機体ジャーナル対応前の実行。30分バケットの操作ログ・荷役スイッチ・エラーから再構成した推定値"
                      >
                        再構成
                      </span>
                    )}
                    {run.requestedBy && (
                      <span className="ml-2 text-xs text-gray-400">
                        {run.requestedBy}
                      </span>
                    )}
                    {compact && run.durationSec != null && (
                      <span className="block text-xs text-gray-400 mt-0.5">
                        {formatDuration(run.durationSec * 1000)} /{' '}
                        {run.tasks.length}タスク
                        {run.pausedSec > 0 &&
                          ` / 一時停止 ${formatDuration(run.pausedSec * 1000)}`}
                      </span>
                    )}
                  </td>
                  {!compact && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                      {run.tasks.length}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {reason ? (
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${reason.className}`}
                      >
                        {reason.label}
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        実行中
                      </span>
                    )}
                  </td>
                  {!compact && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                      {run.durationSec != null
                        ? formatDuration(run.durationSec * 1000)
                        : '－'}
                      {run.pausedSec > 0 && (
                        <span className="block text-xs text-gray-400">
                          一時停止 {formatDuration(run.pausedSec * 1000)}
                        </span>
                      )}
                    </td>
                  )}
                  <td
                    className={`px-4 py-3 whitespace-nowrap text-sm text-right ${
                      run.errors.length > 0
                        ? 'text-orange-500 font-medium'
                        : 'text-gray-400'
                    }`}
                  >
                    {run.errors.length}
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${run.key}-detail`}>
                    <td colSpan={colCount} className="px-6 py-4 bg-gray-50">
                      {/* シナリオ詳細 */}
                      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm mb-4">
                        {run.isScenario && (
                          <>
                            <div>
                              <dt className="text-xs text-gray-400">
                                シナリオ
                              </dt>
                              <dd className="text-gray-900">
                                {run.scenarioName || '不明'}
                                {run.scenarioId &&
                                  run.scenarioId !== run.scenarioName && (
                                    <span className="ml-1 text-xs text-gray-400">
                                      ({run.scenarioId})
                                    </span>
                                  )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-400">
                                実行モード
                              </dt>
                              <dd className="text-gray-900">
                                {EXECUTION_MODE_LABELS[run.executionMode] ||
                                  run.executionMode ||
                                  '－'}
                                {run.isPlaybackScenario && (
                                  <span className="ml-1 text-xs text-gray-400">
                                    (プレイバック)
                                  </span>
                                )}
                              </dd>
                            </div>
                          </>
                        )}
                        <div>
                          <dt className="text-xs text-gray-400">実行者</dt>
                          <dd className="text-gray-900">
                            {run.requestedBy || trigger.label}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-400">時間</dt>
                          <dd className="text-gray-900">
                            {formatTime(run.startTs)} 〜 {formatTime(run.endTs)}
                            {run.durationSec != null && (
                              <span className="ml-1 text-xs text-gray-400">
                                ({formatDuration(run.durationSec * 1000)})
                              </span>
                            )}
                          </dd>
                        </div>
                        {run.pauseCount > 0 && (
                          <div>
                            <dt className="text-xs text-gray-400">一時停止</dt>
                            <dd className="text-gray-900">
                              {run.pauseCount} 回 /{' '}
                              {formatDuration(run.pausedSec * 1000)}
                            </dd>
                          </div>
                        )}
                      </dl>
                      {/* 実走行パス (機体ジャーナルが記録した実際の走行経路) */}
                      {run.path && run.path.length > 1 && (
                        <div className="max-w-lg mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            実走行パス
                          </p>
                          <PreviewPathCanvas
                            trajectories={[
                              {
                                id: run.key,
                                slots: [],
                                action: 'run',
                                path: run.path,
                              },
                            ]}
                          />
                        </div>
                      )}
                      {runLogs?.[run.key] && (
                        <RunScenarioDetail
                          log={runLogs[run.key]}
                          onOpenLog={onOpenLog}
                        />
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-700 mb-2">
                            タスク ({run.tasks.length})
                          </p>
                          <ol className="space-y-1">
                            {run.tasks.map((task, i) => (
                              <li
                                key={`${task.ts}-${i}`}
                                className="flex gap-3 text-gray-700"
                              >
                                <button
                                  onClick={() => onSeek?.(new Date(task.ts))}
                                  className={`tabular-nums ${
                                    onSeek
                                      ? 'text-primary-600 hover:text-primary-700 hover:underline'
                                      : 'text-gray-400'
                                  }`}
                                  title="この時刻に再生位置を移動"
                                >
                                  {formatTime(task.ts)}
                                </button>
                                <span>
                                  {taskLabel(
                                    task.taskType,
                                    task.srcId,
                                    task.dstId
                                  )}
                                </span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        {run.errors.length > 0 && (
                          <div>
                            <p className="font-medium text-gray-700 mb-2">
                              実行中のエラー ({run.errors.length})
                            </p>
                            <ol className="space-y-1">
                              {run.errors.map((err, i) => (
                                <li
                                  key={`${err.ts}-${i}`}
                                  className="flex gap-3 text-gray-700"
                                >
                                  <button
                                    onClick={() => onSeek?.(new Date(err.ts))}
                                    className={`tabular-nums self-start ${
                                      onSeek
                                        ? 'text-primary-600 hover:text-primary-700 hover:underline'
                                        : 'text-gray-400'
                                    }`}
                                    title="この時刻に再生位置を移動"
                                  >
                                    {formatTime(err.ts)}
                                  </button>
                                  <span
                                    className={`inline-flex px-1.5 rounded text-xs font-medium self-center ${
                                      (err.level ?? 0) >= 2
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                  >
                                    {err.code}
                                  </span>
                                  <span className="truncate max-w-md">
                                    {err.message}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
